import type { NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { CURRENTLY_FUNDING_INVESTMENT_BANNER_ID } from '@/constants/funding'
import { paths } from '@/constants/paths'
import { LandingProvider } from '@/contexts/LandingContext'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { Seo } from '@/layout/Seo'
import { AnnouncementModal } from '@/organisms/AnnouncementModal'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import {
  getCollection,
  GUILD_MEMBERSHIP_NUMBERS_ID,
  GUILD_PREDICTS_WINNERS_ENTRY_ID,
} from '@/services/CmsService/Collection'
import { getInvestmentBanner, InvestmentBannerResult } from '@/services/CmsService/InvestmentBanner'
import { getSweepstakes } from '@/services/SweepstakeService/SweepstakeService'
import { Collection, GuildSignupTicketsSection, GuildTestimonialSection } from '@/types/codegen-contentful'
import { SweepstakeObject, TheatricalReleaseObject } from '@/types/codegen-federation'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import {
  getGuildApprovedTorchPosters,
  GuildSignupContextProvider,
} from '@/views/GuildSignupView/GuildSignupContext/GuildSignupContext'
import { NewLandingView } from '@/views/LandingView'
import { defaultGuildTestimonials, defaultTicketSection } from '@/views/LandingView/NewLandingView'
import { getNewTheatricalLandingReleases } from '@/views/TicketsLandingView/NewTicketsLandingView/queries'

interface HomeProps {
  locale: string
  preview: boolean
  investmentBanner: InvestmentBannerResult | null
  members?: number
  lastUpdated?: string
  releases?: TheatricalReleaseObject[]
  sweepstakes?: SweepstakeObject[]
  guildApprovedTorchPosters?: string[]
  guildPredictsWinners?: Collection
}

const Home: NextPage<HomeProps> = ({
  locale,
  preview,
  members,
  investmentBanner,
  lastUpdated,
  releases,
  sweepstakes,
  guildApprovedTorchPosters,
  guildPredictsWinners,
}) => {
  const { t } = useTranslate('home')
  const url = `${getBasePath(locale)}${paths.home}`
  const title = t('angelHomeMetaTitleV2', 'Angel: Stream Original, Uplifting Shows for the Whole Family')
  const description = t(
    'angelStudiosHomeMetaDescriptionV2',
    'Join the Guild to get unlimited access to films and TV series that amplify light through high-quality, fan-supported entertainment.',
  )

  return (
    <>
      <Seo
        canonical={url}
        title={title}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.index}
      />
      <LandingProvider members={members} lastUpdated={lastUpdated} releases={releases} sweepstakes={sweepstakes}>
        <ThemeContextProvider isDarkMode>
          <MarketingVideoPopup />
          <AnnouncementModal />
          <GuildSignupContextProvider
            guildPredictsWinners={guildPredictsWinners}
            showSocialProof
            guildTestimonialSection={defaultGuildTestimonials as GuildTestimonialSection}
            guildTicketsSection={defaultTicketSection as GuildSignupTicketsSection}
            guildApprovedTorchPosters={guildApprovedTorchPosters}
            votingPhoneImageUrl={'/v1756840706/angel-studios/landing/Redesign/device-voting-face-right.webp'}
          >
            <NewLandingView investmentBanner={investmentBanner} preview={preview} />
          </GuildSignupContextProvider>
        </ThemeContextProvider>
      </LandingProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsErrorHandler<HomeProps>(
  { filePath: 'pages/[locale]/home/index.tsx' },
  async ({ locale, preview, federationClient }) => {
    const [
      investmentBanner,
      guildMembershipNumbers,
      guildPredictsWinners,
      releases,
      sweepstakes,
      guildApprovedTorchPosters,
      translations,
    ] = await Promise.all([
      getInvestmentBanner({ locale, preview }, CURRENTLY_FUNDING_INVESTMENT_BANNER_ID),
      getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID),
      getCollection({ locale, preview }, GUILD_PREDICTS_WINNERS_ENTRY_ID),
      getNewTheatricalLandingReleases({ client: federationClient }),
      getSweepstakes(federationClient, ['LIVE']),
      getGuildApprovedTorchPosters(),
      loadTranslations(locale, [
        'common',
        'home',
        'app-promo',
        'account',
        'guild',
        'watch',
        'tickets',
        'join-the-guild',
      ]),
    ])

    const members = Number(guildMembershipNumbers?.customData[0]?.numSubscribers.replace(/,/g, '')) ?? null
    const lastUpdated = guildMembershipNumbers?.customData[0]?.updatedAt ?? null

    return {
      props: {
        locale,
        preview,
        investmentBanner: investmentBanner || null,
        members,
        lastUpdated,
        releases,
        sweepstakes,
        guildApprovedTorchPosters,
        guildPredictsWinners,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default Home
